import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { acceptOutcomeProposal, fetchOutcomeQueue, saveOutcomeReview } from '@/services/admin';
import type { OutcomeQueueItem } from '@/services/types';

/**
 * The CADE outcome-review queue: decisions whose outcome code the ata's own words put in doubt.
 *
 * Suspect rules decide what a person should read (an approval "condicionada" coded otherwise, a
 * TCC code over an item that never mentions a commitment, a final decision with no clause...).
 * A review names the codes, the ata's words that justify them and a note. The platform stores
 * it and never writes the agency database: the file this page downloads is the ETL's
 * `outcome_overrides.json`, which the ETL merges and applies, and a row reads "applied" only
 * once the archive itself carries the reviewed codes. A drafted proposal (loaded unconfirmed by
 * `app.scripts.load_outcome_proposals`) shows pre-ticked with its quote; it reaches the file only
 * once a person accepts it as it stands or saves their own review over it.
 */
export function AdminOutcomeReviewPage() {
  const { t } = useTranslation(['admin', 'cade']);
  const [rule, setRule] = useState('');
  // The atas rebuilt from SEI (2012-2017) add hundreds of items with no code; they are a queue
  // of their own, so the page opens on the portal's atas and offers the others by choice.
  const [source, setSource] = useState('site');
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin', 'outcomes'],
    queryFn: () => fetchOutcomeQueue(),
  });

  if (isPending) return <SkeletonLoader rows={6} />;
  if (error) return <ErrorMessage message={error.message} onRetry={() => void refetch()} />;

  const sources = Array.from(new Set(data.items.map((item) => item.source ?? 'site')));
  const items = data.items.filter(
    (item) =>
      (!rule || item.rules.includes(rule)) && (!source || (item.source ?? 'site') === source),
  );

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('admin:outcomes.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('admin:outcomes.subtitle')}</p>
      </header>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-foreground" data-outcomes-summary>
          {t('admin:outcomes.summary', { total: data.total, reviewed: data.reviewed })}
          {data.proposed ? ` ${t('admin:outcomes.proposedSummary', { count: data.proposed })}` : null}
        </p>
        <ExportButton
          path="/api/v1/admin/cade/outcomes/overrides.json"
          label={t('admin:outcomes.download')}
        />
      </div>
      <p className="text-xs text-muted-foreground">{t('admin:outcomes.applyHelp')}</p>

      <label className="block w-fit space-y-1">
        <span className="text-xs font-medium text-foreground">{t('admin:outcomes.rule')}</span>
        <select
          value={rule}
          onChange={(event) => setRule(event.target.value)}
          className="rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        >
          <option value="">{t('admin:outcomes.allRules')}</option>
          {Object.entries(data.rules).map(([key, count]) => (
            <option key={key} value={key}>
              {t(`admin:outcomes.rules.${key}`)} ({count})
            </option>
          ))}
        </select>
      </label>

      {sources.length > 1 ? (
        <label className="block w-fit space-y-1">
          <span className="text-xs font-medium text-foreground">{t('admin:outcomes.source.label')}</span>
          <select
            value={source}
            onChange={(event) => setSource(event.target.value)}
            className="rounded-md border bg-background px-3 py-2 text-sm text-foreground"
            data-outcome-source
          >
            <option value="">{t('admin:outcomes.source.all')}</option>
            {sources.map((key) => (
              <option key={key} value={key}>
                {t(`admin:outcomes.source.${key}`, { defaultValue: key })} (
                {data.items.filter((item) => (item.source ?? 'site') === key).length})
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <ol className="space-y-3">
        {items.map((item) => (
          <QueueRow key={item.decision_key} item={item} vocabulary={data.vocabulary} />
        ))}
      </ol>
    </div>
  );
}

function QueueRow({ item, vocabulary }: { item: OutcomeQueueItem; vocabulary: string[] }) {
  const { t } = useTranslation(['admin', 'cade']);
  const queryClient = useQueryClient();
  const [codes, setCodes] = useState<string[]>(item.review?.codes ?? item.codes);
  const [evidence, setEvidence] = useState(item.review?.evidence ?? '');
  const [note, setNote] = useState(item.review?.note ?? '');
  const label = (code: string) => t(`cade:overview.outcomes.${code}`, { defaultValue: code });

  const save = useMutation({
    mutationFn: () => saveOutcomeReview(item.decision_key, { codes, evidence, note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'outcomes'] }),
  });
  const accept = useMutation({
    mutationFn: () => acceptOutcomeProposal(item.decision_key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'outcomes'] }),
  });
  const proposal = item.review && item.review.confirmed === false ? item.review : null;

  const toggle = (code: string) =>
    setCodes((current) =>
      current.includes(code) ? current.filter((c) => c !== code) : [...current, code],
    );
  // The evidence must be the ata's own words: the easiest way to get them exactly is to select
  // them in the clause and take the selection.
  const takeSelection = () => {
    const picked = window.getSelection()?.toString().trim();
    if (picked) setEvidence(picked);
  };

  return (
    <li className="space-y-3 rounded-lg border bg-card p-4 text-sm" data-outcome-item={item.decision_key}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Link
          to={`/cade/c/${item.case_key}`}
          className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
        >
          {item.case_number}
        </Link>
        <span className="text-xs text-muted-foreground">
          {t('admin:outcomes.item', { item: item.item_number ?? '—', year: item.session_year ?? '—' })}
        </span>
        {item.is_final ? (
          <span className="rounded border px-1.5 text-xs text-foreground">{t('admin:outcomes.final')}</span>
        ) : null}
        {item.rules.map((rule) => (
          <span key={rule} className="rounded bg-muted px-1.5 text-xs text-foreground">
            {t(`admin:outcomes.rules.${rule}`)}
          </span>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        {t('admin:outcomes.current')}{' '}
        <span className="text-foreground">
          {item.codes.length > 0 ? item.codes.map(label).join('; ') : t('admin:outcomes.none')}
        </span>
      </p>

      {proposal ? (
        <div className="space-y-1 rounded-md border border-dashed p-3" data-outcome-proposal>
          <p className="flex items-center gap-1 text-xs font-medium text-foreground">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {t('admin:outcomes.proposal.title')}
          </p>
          <p className="text-xs text-foreground">
            {proposal.codes.map(label).join('; ')}
          </p>
          <blockquote className="border-l-2 pl-3 text-xs italic text-foreground">“{proposal.evidence}”</blockquote>
          {proposal.note ? <p className="text-xs text-muted-foreground">{proposal.note}</p> : null}
          <p className="text-xs text-muted-foreground">{t('admin:outcomes.proposal.help')}</p>
        </div>
      ) : null}

      {item.clause ? (
        <blockquote className="whitespace-pre-line border-l-2 pl-3 text-foreground" data-outcome-clause>
          {item.clause}
        </blockquote>
      ) : (
        <p className="text-xs text-muted-foreground">{t('admin:outcomes.noClause')}</p>
      )}

      <fieldset className="space-y-1">
        <legend className="text-xs font-medium text-foreground">{t('admin:outcomes.codes')}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {vocabulary.map((code) => (
            <label key={code} className="flex items-center gap-1 text-xs text-foreground">
              <input
                type="checkbox"
                checked={codes.includes(code)}
                onChange={() => toggle(code)}
                className="h-3.5 w-3.5 rounded border"
              />
              {label(code)}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">{t('admin:outcomes.evidence')}</span>
        <textarea
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
          rows={2}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        />
      </label>
      {item.clause ? (
        <button
          type="button"
          onClick={takeSelection}
          className="text-xs text-primary underline-offset-4 hover:underline"
        >
          {t('admin:outcomes.useSelection')}
        </button>
      ) : null}
      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">{t('admin:outcomes.note')}</span>
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        />
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending || codes.length === 0 || evidence.trim().length < 3}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
        >
          {t('admin:outcomes.save')}
        </button>
        {proposal ? (
          <button
            type="button"
            onClick={() => accept.mutate()}
            disabled={accept.isPending}
            className="rounded-md border px-3 py-1.5 text-xs font-medium text-foreground disabled:opacity-50"
            data-outcome-accept
          >
            {t('admin:outcomes.proposal.accept')}
          </button>
        ) : null}
        {accept.error ? (
          <span role="alert" className="text-xs text-destructive">
            {accept.error instanceof ApiError ? accept.error.message : t('admin:outcomes.saveFailed')}
          </span>
        ) : null}
        {save.error ? (
          <span role="alert" className="text-xs text-destructive">
            {save.error instanceof ApiError ? save.error.message : t('admin:outcomes.saveFailed')}
          </span>
        ) : null}
        {item.review && !proposal ? (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-outcome-status>
            {item.applied ? (
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Clock className="h-3.5 w-3.5" aria-hidden />
            )}
            {t(item.applied ? 'admin:outcomes.applied' : 'admin:outcomes.pending', {
              reviewer: item.review.reviewer,
              date: item.review.reviewed_at.slice(0, 10),
            })}
          </span>
        ) : null}
      </div>
    </li>
  );
}
